import React from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { Box } from '@chakra-ui/react';

import {
  FormInputsType,
  NextAction,
  PaymentElementResponse,
} from '../../../@types';
import { sanitizeErrors } from '../../../utils/errors';
import { useTranslations } from '../../../translations/useTranslations';
import { useCommonOptionsStore } from '../../../store';
import { queryPaymentMethodHash } from '../../../queries/methodHash';
import { useNextActionHandler } from '../../../handlers/nextActionHandler';
import { affirmPaymentMethodHashPayloadFactory } from '../../../factories/query-payment-method-hash-payload/affirmPaymentMethodHashPayloadFactory';

import type { PaymentFormWithStripeProps } from '../../../propTypes';
import type { ProcessorsType } from '../../../constants';

export type AffirmFormPropTypes = PaymentFormWithStripeProps & {
  processor: ProcessorsType;
};

const AffirmForm = React.forwardRef(
  (
    { onLoadStart, onLoadEnd, onError, onSubmit }: AffirmFormPropTypes,
    forwardRef: any,
  ) => {
    const stripe = useStripe();
    const elements = useElements();

    const lang = useCommonOptionsStore(state => state.lang);
    const propertyId = useCommonOptionsStore(state => state.propertyId);
    const apiKey = useCommonOptionsStore(state => state.apiKey);
    const { apiUrl } = useCommonOptionsStore();

    const [t] = useTranslations(lang);

    const formMethods = useForm<FormInputsType>({
      mode: 'onChange',
    });
    const nextActionHandler = useNextActionHandler();

    const { handleSubmit, formState } = formMethods;

    const { errors, isLoading, isSubmitting } = formState;

    const errCount = React.useMemo(
      () => (errors ? Object.keys(errors).length : 0),
      [errors],
    );

    React.useEffect(() => {
      if (onLoadStart && isLoading) onLoadStart();
      if (onLoadEnd && isLoading) onLoadEnd();
    }, [isLoading, onLoadStart, onLoadEnd]);

    React.useEffect(() => {
      if (onLoadStart && isSubmitting) onLoadStart();
      if (onLoadEnd && isSubmitting) onLoadEnd();
    }, [isSubmitting, onLoadStart, onLoadEnd]);

    React.useEffect(() => {
      if (onError && errCount) onError(sanitizeErrors(errors));
    }, [onError, errors, errCount]);

    const layoutProps: Record<string, any> = {
      gap: 6,
      columnGap: 4,
    };

    const submitHandler: SubmitHandler<FormInputsType> = async (
      formData: any,
    ) => {
      const data = { ...formData };

      if (!stripe || !elements) return;

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'affirm',
      });

      if (onError && error) {
        onError({
          createPaymentMethod: error.message || t('paymentMethodError'),
        });
      }

      // Get payment method hash
      const { hash: paymentMethodHash, error: hashError } =
        await queryPaymentMethodHash(
          +propertyId,
          apiKey,
          affirmPaymentMethodHashPayloadFactory(paymentMethod.id),
          apiUrl,
        );
      if (hashError && onError) onError(error);

      if (onSubmit) {
        const responseData: PaymentElementResponse = {
          ...data,
          paymentMethod: 'affirm',
          paymentMethodId: paymentMethod.id,
          paymentMethodHash,
        };

        const nextAction: NextAction = await onSubmit({ ...responseData });
        await nextActionHandler(nextAction);
      }
    };

    const submitErrorHandler: SubmitHandler<FormInputsType> = async () => {
      if (onError) onError(sanitizeErrors(errors));
    };

    React.useImperativeHandle(forwardRef, () => ({
      submit() {
        handleSubmit(submitHandler, submitErrorHandler)();
      },
      async validate() {
        return true;
      },
    }));

    return (
      <>
        <FormProvider {...formMethods}>
          <Box paddingTop={(layoutProps as any).gap}>{t('affirmMessage')}</Box>
        </FormProvider>
      </>
    );
  },
);

export { AffirmForm };
